<template>
    <div>
        <b-list-group class="list-group-accent">
            <!-- FOCAL POINT -->
            <c-settings-list
                :title="$t('targets.focalPoint')"
                :subtitle="$t('targets.focalPointGoalsModalTitle')"
                :description="$t('targets.informations.descriptionToAssignThisGoal')"
            >
                <c-form-switch
                    slot="actionButton"
                    :value.sync="onlyFocalPoint"
                    v-if="canCreateAsFocalPoint"
                    @change="onChangeIsFocalPointTarget"
                />
            </c-settings-list>

            <c-settings-list>
                <button
                    slot="actionButton"
                    @click="openFocalPointModal"
                    type="button"
                    class="float-right btn btn-ghost-primary btn-pill btn-sm"
                    v-if="onlyFocalPoint && canCreateAsFocalPoint"
                >
                    {{ $t('targets.informations.toAssign') }}
                </button>
                <div>
                    <div v-if="departmentsFocalPointRelations.length">
                        <small class="text-muted mr-3">
                            <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                            <strong>{{ $t('targets.appliedToTeams') }}:</strong>
                        </small>
                        <div>
                            <b-badge
                                v-for="departmentsFocalPointRelation in departmentsFocalPointRelations"
                                :key="departmentsFocalPointRelation.id"
                                class="mr-1"
                                variant="primary"
                                pill
                            >
                                {{ departmentsFocalPointRelation.title }}
                            </b-badge>
                        </div>
                    </div>
                    <div v-if="occupationGroupsFocalPointRelations.length">
                        <small class="text-muted mr-3">
                            <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                            <strong>{{ $t('targets.appliedToOccupationGroups') }}:</strong>
                        </small>
                        <div>
                            <b-badge
                                v-for="occupationGroupsFocalPointRelation in occupationGroupsFocalPointRelations"
                                :key="occupationGroupsFocalPointRelation.id"
                                class="mr-1"
                                variant="primary"
                                pill
                            >
                                {{ occupationGroupsFocalPointRelation.title }}
                            </b-badge>
                        </div>
                    </div>
                    <div v-if="userFocalPointRelations.length">
                        <small class="text-muted mr-3">
                            <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                            <strong>{{ $t('targets.appliedForSelectedCollaborators') }}:</strong>
                        </small>
                        <div class="avatars-stack mt-2">
                            <div class="avatars-stack mt-2">
                                <div
                                    v-for="userFocalPointRelation in userFocalPointRelations"
                                    :key="userFocalPointRelation.id"
                                    class="avatar align-middle"
                                >
                                    <avatar
                                        :size="30"
                                        :src="userFocalPointRelation.picture"
                                        :username="userFocalPointRelation.name"
                                        :title="userFocalPointRelation.name"
                                        slot="aside"
                                        class="img-avatar"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </c-settings-list>

            <!-- DESDOBRAMENTO E CONTRATAÇÃO DE METAS -->
            <c-settings-list
                :title="$t('targets.cascadingAndHiringGoals')"
                :subtitle="$t('targets.libraryTarget')"
                :description="$t('targets.informations.descriptionLibraryTarget')"
            >
                <c-form-switch
                    slot="actionButton"
                    :value.sync="isLibraryTarget"
                    v-if="canCreateLibraryTarget"
                    @change="onChangeIsLibraryTarget"
                />
            </c-settings-list>

            <c-settings-list
                :subtitle="$t('targets.informations.subtitleSuggestThisGoal')"
                :description="$t('targets.informations.descriptionSuggestThisGoal')"
            >
                <button
                    slot="actionButton"
                    @click="openCascadingAndHiringGoalsModal"
                    type="button"
                    class="float-right btn btn-ghost-primary btn-pill btn-sm"
                    v-if="isLibraryTarget && canCreateLibraryTarget"
                >
                    {{ $t('targets.informations.suggest') }}
                </button>
                <div>
                    <div v-if="departments.length">
                        <small class="text-muted mr-3">
                            <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                            <strong>{{ $t('targets.appliedToTeams') }}:</strong>
                        </small>
                        <div>
                            <b-badge
                                v-for="department in departments"
                                :key="department.id"
                                class="mr-1"
                                variant="primary"
                                pill
                            >
                                {{ department.title }}
                            </b-badge>
                        </div>
                    </div>
                    <div v-if="occupationGroups.length">
                        <small class="text-muted mr-3">
                            <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                            <strong>{{ $t('targets.appliedToOccupationGroups') }}:</strong>
                        </small>
                        <div>
                            <b-badge
                                v-for="occupationGroup in occupationGroups"
                                :key="occupationGroup.id"
                                class="mr-1"
                                variant="primary"
                                pill
                            >
                                {{ occupationGroup.title }}
                            </b-badge>
                        </div>
                    </div>
                    <div v-if="userRelations.length">
                        <small class="text-muted mr-3">
                            <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                            <strong>{{ $t('targets.appliedForSelectedCollaborators') }}:</strong>
                        </small>
                        <div class="avatars-stack mt-2">
                            <div
                                v-for="userRelation in userRelations"
                                :key="userRelation.id"
                                class="avatar align-middle"
                            >
                                <avatar
                                    :size="30"
                                    :src="userRelation.picture"
                                    :username="userRelation.name"
                                    :title="userRelation.name"
                                    slot="aside"
                                    class="img-avatar"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </c-settings-list>

            <!-- FÓRMULA -->
            <c-settings-list
                :title="$t('targets.automaticCalculation')"
                :subtitle="$t('targets.enableAutomaticCalculation')"
                description=""
            >
                <button
                    v-if="isUserSuperUser || !targetIsCompleted"
                    slot="actionButton"
                    @click="openAutomaticCalculationModal"
                    type="button"
                    class="float-right btn btn-ghost-primary btn-pill btn-sm"
                >
                    {{ $t('form.button.edit') }}
                </button>
                <b-card v-if="target.formulaHtml" class="custom-formula-html mt-4 text-justify">
                    <span v-html="formulaHtml"></span>
                </b-card>
            </c-settings-list>
        </b-list-group>
    </div>
</template>

<script>
import api from '@/services/api'
import notification, { success, danger } from '@/lib/notification'
import i18n from '@/i18n'
import { FormSwitch as CFormSwitch } from '@/views/components/fields'
import CSettingsList from '@/views/target/components/view/tab-content/settings-list'
import Avatar from 'vue-avatar'
import Swal from 'sweetalert2'
import { mapGetters } from 'vuex'
import { userPermissionsMixin } from '@/mixins'

notification.init()

export default {
    name: 'Settings',
    props: ['target', 'currentEventPermissions'],
    mixins: [userPermissionsMixin],
    components: {
        CSettingsList,
        CFormSwitch,
        Avatar,
    },
    data() {
        return {
            onlyFocalPoint: !!this.target.onlyFocalPoint,
            isLibraryTarget: !!this.target.targetLibrary,
        }
    },
    methods: {
        openCascadingAndHiringGoalsModal() {
            this.$router.push({
                path: `/v2/target/targets/view/${this.target.id}/suggest`,
            })
        },
        openFocalPointModal() {
            this.$router.push({
                path: `/v2/target/targets/view/${this.target.id}/focal-point`,
            })
        },
        openAutomaticCalculationModal() {
            this.$router.push({
                path: `/v2/target/targets/view/${this.target.id}/automatic-calculation`,
            })
        },
        notificationEditedSuccessfully() {
            success(
                i18n.t('notification.editedSuccessfully', {
                    item: i18n.t('targets.target'),
                })
            )
        },
        notificationError() {
            danger()
        },
        updateTargetLibrary(value) {
            api.put(`/goals/${this.target.id}/update-target-library`, {
                target_library: value,
            })
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp
                    this.$emit('update-parent', 'updateSuggest', data)
                    this.notificationEditedSuccessfully()
                })
                .catch((error) => {
                    console.log(error)
                    this.notificationError()
                })
        },
        onChangeIsLibraryTarget(value) {
            if (value) {
                this.updateTargetLibrary(true)
            } else {
                Swal({
                    type: 'warning',
                    title: i18n.t('notification.warning'),
                    text: i18n.t('targets.informations.descriptionLibraryTargetConfirm'),
                    confirmButtonText: i18n.t('notification.yes'),
                    cancelButtonText: i18n.t('notification.no'),
                    showCancelButton: true,
                })
                    .then((answer) => {
                        if (answer.value == true) {
                            return this.updateTargetLibrary(false)
                        } else {
                            this.isLibraryTarget = true
                        }
                    })
                    .catch((err) => console.error(err))
            }
        },
        updateFocalPoint(value) {
            api.put(`/goals/${this.target.id}/update-focal-point`, {
                only_focal_point: value,
            })
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp
                    this.$emit('update-parent', 'updateOnlyFocalPoint', data)
                    this.notificationEditedSuccessfully()
                })
                .catch((error) => {
                    console.log(error)
                    this.notificationError()
                })
        },
        onChangeIsFocalPointTarget(value) {
            if (value) {
                this.updateFocalPoint(true)
            } else {
                Swal({
                    type: 'warning',
                    title: i18n.t('notification.warning'),
                    text: i18n.t('targets.informations.descriptionFocaPointConfirm'),
                    confirmButtonText: i18n.t('notification.yes'),
                    cancelButtonText: i18n.t('notification.no'),
                    showCancelButton: true,
                })
                    .then((answer) => {
                        if (answer.value == true) {
                            return this.updateFocalPoint(false)
                        } else {
                            this.onlyFocalPoint = true
                        }
                    })
                    .catch((err) => console.error(err))
            }
        },
        wrapMathOperators(formulaHtml, operator) {
            const html = `<span class="badge badge-primary mr-1"><i class="fas fa-${operator}"></i></span>`
            let mathOperator
            switch (operator) {
                case 'plus':
                    mathOperator = '+'
                    break
                case 'minus':
                    mathOperator = '-'
                    break
                case 'times':
                    mathOperator = '*'
                    break
                case 'divide':
                    mathOperator = '/'
                    break

                default:
                    break
            }
            const regex = new RegExp(`(\\${mathOperator})(?![^<]*>|[^<>]*<\\/)`, 'gm')
            return formulaHtml.replace(regex, html)
        },
        wrapParentheses(formulaHtml) {
            const pharentesesTag = `<strong class="mr-1">$1</strong>`
            const regexMathPharenteses = /(\(|\))(?![^<]*>|[^<>]*<\/)/gm
            return formulaHtml.replace(regexMathPharenteses, pharentesesTag)
        },
        wrapNumbers(formulaHtml) {
            const badgeTag = `<span class="badge badge-secondary mr-1">$1</span>`
            const regexOnlyNumbers = /[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)(?![^<]*>|[^<>]*<\/)/gm
            return formulaHtml.replace(regexOnlyNumbers, badgeTag)
        },
    },
    computed: {
        ...mapGetters({
            currentUser: 'getCurrentUser',
        }),
        isUserSuperUser() {
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_target_master']
            return this.userHasPermission(this.currentUser, availableRoles, [])
        },
        departments() {
            return this.target.departments || []
        },
        occupationGroups() {
            return this.target.occupationGroups || []
        },
        userRelations() {
            return this.target.userRelations || []
        },
        canCreateAsFocalPoint() {
            return (
                this.isUserSuperUser ||
                (this.currentEventPermissions[this.target.level].create_as_focal_point && !this.targetIsCompleted)
            )
        },
        canCreateLibraryTarget() {
            return (
                this.isUserSuperUser ||
                (this.currentEventPermissions[this.target.level].create_library_goal && !this.targetIsCompleted)
            )
        },
        targetIsCompleted() {
            return this.target.status === 'completed'
        },
        userFocalPointRelations() {
            return this.target.userFocalPointRelations || []
        },
        departmentsFocalPointRelations() {
            return this.target.departmentsFocalPointRelations || []
        },
        occupationGroupsFocalPointRelations() {
            return this.target.occupationGroupsFocalPointRelations || []
        },
        formulaHtml() {
            const regexWithoutPTag = /(<p>|<\/p>)(?![^<]*>|[^<>]*<\/)/gm
            let formulaHtml = this.target.formulaHtml.replace(regexWithoutPTag, '')

            let operators = ['plus', 'minus', 'times', 'divide']
            operators.forEach((operator) => {
                formulaHtml = this.wrapMathOperators(formulaHtml, operator)
            })

            formulaHtml = this.wrapParentheses(formulaHtml)
            formulaHtml = this.wrapNumbers(formulaHtml)

            const regexWhiteSpaces = /(\s)(?![^<]*>|[^<>]*<\/)/g
            formulaHtml = formulaHtml.replace(regexWhiteSpaces, '')

            return formulaHtml
        },
    },
}
</script>

<style lang="scss" scope>
.custom-formula-html {
    .mention {
        background-color: #dce1e6;
        font-family: Helvetica, Arial, sans-serif;
        font-size: 13px;
        padding: 1px;
        margin-right: 5px;
    }
    .mention:hover {
        background-color: #c8ced3;
    }
}
</style>
